/**
 * Update check + auto-update for Standard Code.
 *
 * Periodically queries the npm registry for the latest available version of
 * @standardagents/code, caches the result locally (6h TTL), and — when the
 * CLI is a real installed copy (not a dev checkout) — installs the new
 * version in a DETACHED background process so the next launch simply runs it.
 * The install goes through the SAME package manager that installed the CLI
 * (npm/pnpm/yarn/bun, detected from the running module's path): updating a
 * pnpm-managed global with npm would fork the install into two competing
 * copies.
 *
 * State about the background attempt is persisted so a failed install
 * degrades to the manual hint instead of retrying on every launch. All
 * checks fall silently on network errors or timeouts so the CLI startup is
 * never delayed or noisy.
 *
 * Opt-outs: STANDARD_CODE_NO_AUTO_UPDATE=1 disables the background install
 * (the availability hint still shows); NO_UPDATE_NOTIFIER/CI disable the
 * version check entirely.
 */
import fs from "node:fs";
import path from "node:path";
import { homedir } from "node:os";
import { spawn } from "node:child_process";
import { fileURLToPath } from "node:url";

const PKG_NAME = "@standardagents/code";
const REGISTRY_URL = `https://registry.npmjs.org/${encodeURIComponent(PKG_NAME)}`;

/** Cache lives under ~/.config/standardagents-cli/update-check.json */
const CACHE_REL_DIR = ".config/standardagents-cli";
const CACHE_FILE = "update-check.json";
/** Background auto-update attempt state (same directory). */
const STATE_FILE = "auto-update.json";
const CACHE_TTL_MS = 1000 * 60 * 60 * 6; // 6 hours

/** Network timeout — don't delay startup more than this. */
const CHECK_TIMEOUT_MS = 4000;

/** How long an in-flight background install is trusted before it's presumed
 *  crashed (never wrote its final state) and eligible to start again. */
const IN_FLIGHT_TTL_MS = 15 * 60 * 1000;

export interface UpdateInfo {
  current: string;
  latest: string;
}

interface CacheEntry {
  latest: string;
  timestamp: number;
}

export type PackageManager = "npm" | "pnpm" | "yarn" | "bun";

/** Persisted record of the most recent background install attempt. */
export interface AutoUpdateState {
  version: string; // the version the attempt tried to install
  startedAt: number;
  exitCode: number | null; // null while (presumed) in flight
  finishedAt?: number;
  output?: string; // tail of the package manager's output (for diagnostics)
}

export type AutoUpdateDecision =
  | "start" // no blocking state — kick off a background install
  | "in_flight" // an attempt for this version started recently and hasn't finished
  | "already_failed" // this version was attempted and the install failed
  | "path_shadowed" // the install "succeeded" but this process still runs the old version
  | "dev_checkout" // running from source, not an installed copy — never auto-update
  | "disabled"; // STANDARD_CODE_NO_AUTO_UPDATE opt-out

// ── cache/state helpers ──────────────────────────────────────────────────────

function cacheDir(): string {
  return path.join(homedir(), CACHE_REL_DIR);
}

function cachePath(): string {
  return path.join(cacheDir(), CACHE_FILE);
}

function readCache(): CacheEntry | null {
  try {
    const raw = fs.readFileSync(cachePath(), "utf-8");
    return JSON.parse(raw) as CacheEntry;
  } catch {
    return null;
  }
}

function writeCache(latest: string): void {
  try {
    const dir = cacheDir();
    if (!fs.existsSync(dir)) fs.mkdirSync(dir, { recursive: true });
    fs.writeFileSync(cachePath(), JSON.stringify({ latest, timestamp: Date.now() }));
  } catch {
    // Fail silently — cache write never crashes the CLI.
  }
}

export function readAutoUpdateState(dir = cacheDir()): AutoUpdateState | null {
  try {
    const raw = fs.readFileSync(path.join(dir, STATE_FILE), "utf-8");
    const state = JSON.parse(raw) as AutoUpdateState;
    return typeof state?.version === "string" ? state : null;
  } catch {
    return null;
  }
}

export function writeAutoUpdateState(state: AutoUpdateState, dir = cacheDir()): void {
  try {
    if (!fs.existsSync(dir)) fs.mkdirSync(dir, { recursive: true });
    fs.writeFileSync(path.join(dir, STATE_FILE), JSON.stringify(state));
  } catch {
    // Fail silently.
  }
}

export function clearAutoUpdateState(dir = cacheDir()): void {
  try {
    fs.unlinkSync(path.join(dir, STATE_FILE));
  } catch {
    // Already gone — fine.
  }
}

// ── package manager detection ────────────────────────────────────────────────

/**
 * Which package manager owns the running install, judged by where this module
 * executes from. Global installs land in manager-specific trees (pnpm's
 * content-addressed `.pnpm`/PNPM_HOME, bun's `.bun/install/global`, yarn's
 * `.config/yarn/global` or `.yarn`), while a path outside any
 * `node_modules/@standardagents/code` means a dev checkout (returns null —
 * never auto-update source trees). npm is the fallback for a plain global
 * node_modules.
 */
export function detectPackageManager(
  selfPath: string = fileURLToPath(import.meta.url),
  env: Record<string, string | undefined> = process.env
): PackageManager | null {
  // Normalize by the PATH STRING's separators (either style), not the host's
  // path.sep — a Windows-style path must parse the same everywhere.
  const norm = selfPath.split(/[\\/]/).join("/");
  if (!norm.includes("node_modules/@standardagents/code")) return null;

  const pnpmHome = (env.PNPM_HOME || "").split(/[\\/]/).join("/");
  if (pnpmHome && norm.startsWith(pnpmHome)) return "pnpm";
  if (norm.includes("/.pnpm/") || /\/pnpm\/global\//.test(norm)) return "pnpm";
  if (norm.includes("/.bun/")) return "bun";
  if (norm.includes("/.config/yarn/") || norm.includes("/.yarn/")) return "yarn";
  return "npm";
}

/** The global-install update invocation for a package manager. */
export function updateCommand(pm: PackageManager): { cmd: string; args: string[]; display: string } {
  switch (pm) {
    case "pnpm":
      return { cmd: "pnpm", args: ["add", "-g", `${PKG_NAME}@latest`], display: `pnpm add -g ${PKG_NAME}@latest` };
    case "yarn":
      return { cmd: "yarn", args: ["global", "add", `${PKG_NAME}@latest`], display: `yarn global add ${PKG_NAME}@latest` };
    case "bun":
      return { cmd: "bun", args: ["add", "-g", `${PKG_NAME}@latest`], display: `bun add -g ${PKG_NAME}@latest` };
    default:
      return { cmd: "npm", args: ["i", "-g", `${PKG_NAME}@latest`], display: `npm i -g ${PKG_NAME}@latest` };
  }
}

// ── auto-update decision + launch ────────────────────────────────────────────

/**
 * Decide what to do about an available update. Pure — all inputs explicit —
 * so the retry/backoff rules are testable: attempt each new version once,
 * treat a recent unfinished attempt as in flight, treat a stale unfinished
 * attempt as crashed (eligible to retry), and never loop on a version that
 * already failed or didn't take effect.
 */
export function decideAutoUpdate(
  info: UpdateInfo,
  opts: {
    state: AutoUpdateState | null;
    pm: PackageManager | null;
    env?: Record<string, string | undefined>;
    now?: number;
  }
): AutoUpdateDecision {
  const env = opts.env ?? process.env;
  if (env.STANDARD_CODE_NO_AUTO_UPDATE) return "disabled";
  if (!opts.pm) return "dev_checkout";

  const state = opts.state;
  if (state && state.version === info.latest) {
    if (state.exitCode === null) {
      const now = opts.now ?? Date.now();
      return now - state.startedAt < IN_FLIGHT_TTL_MS ? "in_flight" : "start";
    }
    // The attempt finished. exitCode 0 but we're still running the old
    // version means the new install isn't what this launcher resolves to
    // (shadowed PATH, multiple installs) — don't reinstall in a loop.
    return state.exitCode === 0 ? "path_shadowed" : "already_failed";
  }
  return "start";
}

/**
 * Kick off the background install: record an in-flight state, then spawn a
 * DETACHED node child that runs the package manager and writes the final
 * state (exit code + output tail) when it finishes. The CLI does not wait —
 * the new version simply applies on the next launch.
 */
export function startBackgroundUpdate(
  latest: string,
  pm: PackageManager,
  dir = cacheDir()
): boolean {
  const startedAt = Date.now();
  writeAutoUpdateState({ version: latest, startedAt, exitCode: null }, dir);
  const stateFile = path.join(dir, STATE_FILE);
  const { cmd, args } = updateCommand(pm);
  // The child is plain `node -e` (no shell quoting risk for the state path or
  // version — both JSON-encoded), detached with no stdio so it survives this
  // process exiting and can never write to our terminal.
  const script =
    "const cp=require('child_process');const fs=require('fs');" +
    `const r=cp.spawnSync(${JSON.stringify(cmd)},${JSON.stringify(args)},{shell:process.platform==='win32',encoding:'utf8'});` +
    "const out=((r.stdout||'')+(r.stderr||'')).slice(-2000);" +
    `fs.writeFileSync(${JSON.stringify(stateFile)},JSON.stringify({version:${JSON.stringify(latest)},startedAt:${startedAt},exitCode:r.status==null?-1:r.status,finishedAt:Date.now(),output:out}));`;
  try {
    const child = spawn(process.execPath, ["-e", script], { detached: true, stdio: "ignore" });
    child.unref();
    return true;
  } catch {
    // Couldn't even spawn — mark failed so the next launch shows the hint.
    writeAutoUpdateState({ version: latest, startedAt, exitCode: -1, finishedAt: Date.now() }, dir);
    return false;
  }
}

/**
 * If a background update for OUR current version completed successfully, the
 * update has been applied — consume (clear) the state and return it so the
 * caller can mention it once. Any state for the current version is cleared
 * either way (it's spent).
 */
export function consumeAppliedUpdate(currentVersion: string, dir = cacheDir()): AutoUpdateState | null {
  const state = readAutoUpdateState(dir);
  if (!state || state.version !== currentVersion) return null;
  clearAutoUpdateState(dir);
  return state.exitCode === 0 ? state : null;
}

// ── version check ────────────────────────────────────────────────────────────

async function fetchLatest(currentVersion: string): Promise<string | null> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), CHECK_TIMEOUT_MS);
  try {
    const res = await fetch(REGISTRY_URL, {
      signal: controller.signal,
      headers: {
        Accept: "application/vnd.npm.install-v1+json; q=1.0, application/json; q=0.8, */*",
        "User-Agent": `${PKG_NAME}/${currentVersion}`,
      },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { "dist-tags"?: { latest?: string } };
    return data["dist-tags"]?.latest ?? null;
  } catch {
    // Offline, timeout, DNS failure, etc. — remain silent.
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Check whether a newer version of this package is available on npm.
 *
 * Results are cached for 6 hours to avoid hammering the registry. On failure
 * (offline, timeout, malformed response) returns `null` — the CLI should never
 * be blocked or noisy about a version check.
 *
 * @param currentVersion — the locally installed version (from package.json).
 * @returns `UpdateInfo` if a newer version exists, or `null` if up-to-date or
 *          the check could not be completed.
 */
/**
 * True when `candidate` is a strictly newer RELEASE than `current` (semver
 * major.minor.patch; a stable release outranks a same-base prerelease). We
 * compare by precedence rather than mere inequality so the notice NEVER
 * suggests a downgrade or a pointless reinstall when the running build is
 * already at or ahead of the registry's `latest` — e.g. a freshly published
 * build read against a stale cache.
 */
export function isNewerVersion(candidate: string, current: string): boolean {
  const parse = (v: string) => {
    const m = v.trim().replace(/^v/, "").match(/^(\d+)\.(\d+)\.(\d+)(?:-(.+))?$/);
    return m ? { nums: [Number(m[1]), Number(m[2]), Number(m[3])], pre: m[4] ?? null } : null;
  };
  const a = parse(candidate);
  const b = parse(current);
  if (!a || !b) return false; // unparseable → never prompt
  for (let i = 0; i < 3; i++) {
    if (a.nums[i] !== b.nums[i]) return a.nums[i] > b.nums[i];
  }
  // Same major.minor.patch: a release is newer than a same-base prerelease.
  return a.pre === null && b.pre !== null;
}

export async function checkForUpdate(currentVersion: string): Promise<UpdateInfo | null> {
  if (!currentVersion) return null;

  // Honour an explicit opt-out.
  if (process.env.NO_UPDATE_NOTIFIER || process.env.CI) return null;

  // Check cache first — skip the network call if we checked recently.
  const cached = readCache();
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return isNewerVersion(cached.latest, currentVersion)
      ? { current: currentVersion, latest: cached.latest }
      : null;
  }

  const latest = await fetchLatest(currentVersion);
  if (!latest) return null;
  writeCache(latest);
  return isNewerVersion(latest, currentVersion) ? { current: currentVersion, latest } : null;
}

/**
 * Force a fresh check, bypassing the local cache.
 * Used by the `/update` slash command so the user always gets a live answer.
 */
export async function forceCheckForUpdate(currentVersion: string): Promise<UpdateInfo | null> {
  if (!currentVersion) return null;
  if (process.env.NO_UPDATE_NOTIFIER || process.env.CI) return null;

  const latest = await fetchLatest(currentVersion);
  if (!latest) return null;
  writeCache(latest);
  return isNewerVersion(latest, currentVersion) ? { current: currentVersion, latest } : null;
}

/**
 * Run the package manager's global update in the FOREGROUND (the `/update`
 * command), capturing output rather than inheriting stdio — a child writing
 * to the terminal would desync the TUI's bottom-region redraw.
 */
export function runUpdate(pm: PackageManager): Promise<{ ok: boolean; output: string }> {
  return new Promise((resolve) => {
    const { cmd, args } = updateCommand(pm);
    const child = spawn(cmd, args, {
      shell: process.platform === "win32",
      stdio: ["ignore", "pipe", "pipe"],
    });
    let out = "";
    child.stdout?.on("data", (d) => (out += d));
    child.stderr?.on("data", (d) => (out += d));
    child.on("close", (code) => resolve({ ok: code === 0, output: out }));
    child.on("error", (err) => resolve({ ok: false, output: String(err) }));
  });
}
